/**
 * Alert Detail Modal Component
 *
 * Displays detailed information about an alert and allows actions.
 *
 * Requirements:
 * - FR-036: Acknowledge and resolve alerts
 * - FR-037: Alert details and history
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAlertDetail, useAcknowledgeAlert, useResolveAlert, Alert } from '../../hooks/useAlerts';
import { formatDistanceToNow, format } from 'date-fns';

interface AlertHistoryEntry {
  action: string;
  timestamp: string;
  details: Record<string, unknown>;
}

interface AlertDetailModalProps {
  alertId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Who acted, for the lifecycle timeline.
 *
 * 🛑 An engine auto-resolve (any non-terminal Spec-193 adapter) leaves
 * `resolved_by` NULL — there is no human actor, by construction. This used to
 * fall through to the literal `'unknown'`, which reads as *data loss*: an
 * operator reasonably infers the actor was recorded and then lost. `'system'`
 * states the truth — nobody resolved it, the engine did — and matches what the
 * `/alerts` list has always shown (`Alerts.tsx`: `resolvedByName ?? 'system'`).
 *
 * Found via Spec 260, whose `index-capture-health` adapter auto-resolves as its
 * NORMAL path, so every one of its alerts reproduced the mismatch.
 */
const actorLabel = (name?: string | null, id?: string | null): string => name ?? id ?? 'system';

export const AlertDetailModal: React.FC<AlertDetailModalProps> = ({ alertId, isOpen, onClose }) => {
  // 🛑 Poll while the modal is OPEN (15s — the same contract the /alerts list has
  // used since Spec 255's "Live" guarantee). Previously this was `useAlertDetail(alertId)`,
  // which resolves to `refetchInterval: false` AND `staleTime: 5 * 60 * 1000`, so the
  // modal never refreshed while open and served a cached copy for five minutes even
  // across close/reopen. The ack/resolve *mutations* invalidate ['alert', id], so a
  // user's OWN action refreshed correctly — but nothing invalidated on a SERVER-side
  // change (engine auto-resolve, escalation, or another operator acting). The result
  // was a modal showing "Unacknowledged" with live Acknowledge/Resolve buttons on an
  // alert the list already showed as Resolved — the exact two-operators-working-blind
  // collision Spec 255 exists to prevent, reintroduced through the detail view.
  const { data: alert, isLoading: loadingAlert } = useAlertDetail(alertId, isOpen, 15000);
  const acknowledgeMutation = useAcknowledgeAlert();
  const resolveMutation = useResolveAlert();

  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');
  const [showResolveForm, setShowResolveForm] = useState(false);
  const [resolution, setResolution] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Movable modal (owner 2026-06-11): drag by the header; position resets per alert.
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);
  useEffect(() => { setDrag({ x: 0, y: 0 }); }, [alertId, isOpen]);
  const onDragStart = (e: React.PointerEvent): void => {
    if ((e.target as HTMLElement).closest('button')) return; // don't hijack header buttons
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: drag.x, baseY: drag.y };
    const move = (ev: PointerEvent): void => {
      const d = dragRef.current;
      if (d) setDrag({ x: d.baseX + ev.clientX - d.startX, y: d.baseY + ev.clientY - d.startY });
    };
    const up = (): void => {
      dragRef.current = null;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  // Collector/structured alerts carry their context in the details JSON.
  const detailEntries: [string, unknown][] = (() => {
    const d = alert?.details as Record<string, unknown> | string | null | undefined;
    if (!d) return [];
    try {
      const obj = typeof d === 'string' ? (JSON.parse(d) as Record<string, unknown>) : d;
      return Object.entries(obj);
    } catch {
      return [];
    }
  })();

  // The alert's lifecycle timeline, synthesised from its own timestamps (there is no
  // separate history table). Gives the expected Created → Acknowledged → Escalated →
  // Resolved entries, with the actor and (for resolve) the resolution notes.
  const historyEntries: AlertHistoryEntry[] = React.useMemo(() => {
    if (!alert) return [];
    const entries: AlertHistoryEntry[] = [];
    if (alert.createdAt) {
      entries.push({ action: 'created', timestamp: alert.createdAt, details: {} });
    }
    if (alert.acknowledgedAt) {
      entries.push({
        action: 'acknowledged',
        timestamp: alert.acknowledgedAt,
        details: { By: actorLabel(alert.acknowledgedByName, alert.acknowledgedBy) },
      });
    }
    if (alert.escalatedAt) {
      entries.push({ action: 'escalated', timestamp: alert.escalatedAt, details: {} });
    }
    if (alert.resolvedAt) {
      // Spec 297 FR-005: the engine stores why it resolved an alert whose entity retired
      // (details._resolvedWhy = 'entity retired', alertLifecycle.resolveRecoveries); shown beside the resolver.
      const details = alert.details as Record<string, unknown> | null | undefined;
      const why = details && typeof details === 'object' ? details._resolvedWhy : undefined;
      entries.push({
        action: 'resolved',
        timestamp: alert.resolvedAt,
        details: {
          By: actorLabel(alert.resolvedByName, alert.resolvedBy),
          ...(why ? { Reason: String(why) } : {}),
          ...(alert.resolutionNotes ? { Notes: alert.resolutionNotes } : {}),
        },
      });
    }
    return entries.sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }, [alert]);

  const getSeverityColor = (severity: string): string => {
    switch (severity.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      case 'medium':
      case 'warning': // owner 2026-06-11: WARNING = yellow
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'info': // owner 2026-06-11: INFO = green
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getStatusColor = (acknowledged: boolean, resolved: boolean): string => {
    if (resolved) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    if (acknowledged) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  };

  const handleAcknowledge = async (): Promise<void> => {
    if (!alertId) return;
    setSubmitting(true);
    try {
      await acknowledgeMutation.mutateAsync({ alertId, payload: { notes: notes || undefined } });
      setNotes('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolve = async (): Promise<void> => {
    if (!alertId || !resolution.trim()) return;
    setSubmitting(true);
    try {
      // Send BOTH the resolution summary and the optional additional notes (previously
      // only the resolution was sent, and under the wrong field name, so both were lost).
      await resolveMutation.mutateAsync({
        alertId,
        payload: { resolution: resolution.trim(), notes: notes.trim() || undefined },
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const formatAlertType = (type: string | undefined): string => {
    if (!type) return 'Unknown';
    return type.split(/(?=[A-Z])/).join(' ');
  };

  if (!isOpen) return null;

  if (loadingAlert) {
    return (
      <div className='fixed inset-0 bg-transparent flex items-center justify-center z-50'>
        <div className='bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] shadow-2xl p-6 max-w-2xl w-full mx-4'>
          <div className='flex items-center justify-center py-12'>
            <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600'></div>
          </div>
        </div>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className='fixed inset-0 bg-transparent flex items-center justify-center z-50'>
        <div className='bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] shadow-2xl p-6 max-w-2xl w-full mx-4'>
          <div className='text-center py-12'>
            <p className='text-[var(--color-text-secondary)]'>Alert not found</p>
            <button
              onClick={onClose}
              className='mt-4 px-4 py-2 bg-[var(--color-bg-subtle)] text-[var(--color-text-secondary)] rounded-md hover:bg-gray-200 dark:hover:bg-gray-600'
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getHistoryIcon = (action: string): JSX.Element => {
    switch (action) {
      case 'created':
        return (
          <div className='w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center'>
            <svg
              className='w-4 h-4 text-blue-600 dark:text-blue-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 6v6m0 0v6m0-6h6m-6 0H6'
              />
            </svg>
          </div>
        );
      case 'acknowledged':
        return (
          <div className='w-8 h-8 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center'>
            <svg
              className='w-4 h-4 text-yellow-600 dark:text-yellow-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
              />
            </svg>
          </div>
        );
      case 'escalated':
        return (
          <div className='w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center'>
            <svg
              className='w-4 h-4 text-orange-600 dark:text-orange-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
              />
            </svg>
          </div>
        );
      case 'resolved':
        return (
          <div className='w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center'>
            <svg
              className='w-4 h-4 text-green-600 dark:text-green-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
        );
      default:
        return (
          <div className='w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center'>
            <svg
              className='w-4 h-4 text-gray-600 dark:text-gray-400'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
        );
    }
  };

  return (
    <div className='fixed inset-0 z-50 overflow-y-auto'>
      <div className='flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0'>
        {/* Backdrop — transparent + non-dismissing (Spec 197) */}
        <div
          className='fixed inset-0 transition-opacity bg-transparent'
        />

        {/* Modal — `relative` is LOAD-BEARING: without it the fixed backdrop paints OVER
            this panel (every click hit the backdrop -> instant close, and the 40% veil
            dimmed the content — the dark-mode readability complaint). */}
        <div
          className='relative inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle bg-[var(--color-bg-card)] border border-[var(--color-border)] shadow-2xl rounded-lg sm:my-16'
          style={{ transform: `translate(${drag.x}px, ${drag.y}px)` }}
        >
          {/* Header (drag handle) */}
          <div
            className='px-6 py-4 bg-[var(--color-bg-subtle)] border-b border-[var(--color-border)] cursor-move select-none'
            onPointerDown={onDragStart}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-3'>
                <h3 className='text-lg font-semibold text-[var(--color-text-primary)]'>Alert Details</h3>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSeverityColor(
                    alert.severity
                  )}`}
                >
                  {alert.severity.toUpperCase()}
                </span>
                <span
                  className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                    !!alert.acknowledgedAt,
                    !!alert.resolvedAt
                  )}`}
                >
                  {alert.resolvedAt ? 'Resolved' : alert.acknowledgedAt ? 'Acknowledged' : 'Unacknowledged'}
                </span>
              </div>
              <button onClick={onClose} className='text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'>
                <svg className='w-6 h-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className='border-b border-[var(--color-border)]'>
            <nav className='flex -mb-px'>
              <button
                onClick={() => setActiveTab('details')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'details'
                    ? 'border-b-2 border-[var(--color-accent)] text-[var(--color-accent)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                Details
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'history'
                    ? 'border-b-2 border-[var(--color-accent)] text-[var(--color-accent)]'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                History
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className='px-6 py-4 max-h-96 overflow-y-auto'>
            {activeTab === 'details' && (
              <div className='space-y-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>Type</label>
                    <p className='text-sm text-[var(--color-text-primary)]'>{formatAlertType(alert.type ?? alert.alertType)}</p>
                  </div>
                  {/* Spec 192: only show the rows that apply — capacity alerts have no
                      lakehouse/workspace; their context lives in the Details JSON below. */}
                  {alert.lakehouseName && (
                    <div>
                      <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>Lakehouse</label>
                      <p className='text-sm text-[var(--color-text-primary)]'>{alert.lakehouseName}</p>
                    </div>
                  )}
                  {alert.workspaceName && (
                    <div>
                      <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>Workspace</label>
                      <p className='text-sm text-[var(--color-text-primary)]'>{alert.workspaceName}</p>
                    </div>
                  )}
                  <div>
                    <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>Created</label>
                    <p className='text-sm text-[var(--color-text-primary)]'>
                      {format(new Date(alert.createdAt), 'PPpp')}
                      <span className='text-[var(--color-text-secondary)] ml-1'>
                        ({formatDistanceToNow(new Date(alert.createdAt), { addSuffix: true })})
                      </span>
                    </p>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>Message</label>
                  <p className='text-sm text-[var(--color-text-primary)] mt-1 p-3 bg-[var(--color-bg-subtle)] rounded-md'>
                    {alert.message}
                  </p>
                </div>

                {/* Structured context (collector alerts: metric, value, threshold, rule…) */}
                {detailEntries.length > 0 && (
                  <div>
                    <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>Details</label>
                    <div className='mt-1 p-3 bg-[var(--color-bg-subtle)] rounded-md grid grid-cols-2 gap-x-4 gap-y-1'>
                      {detailEntries.map(([k, v]) => (
                        <div key={k} className='text-sm'>
                          <span className='text-[var(--color-text-secondary)]'>{k}: </span>
                          <span className='text-[var(--color-text-primary)] break-all'>{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {alert.escalationStatus === 'Escalated' && (
                  <div className='p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md'>
                    <div className='flex items-center gap-2'>
                      <svg
                        className='w-5 h-5 text-orange-600 dark:text-orange-400'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                        />
                      </svg>
                      <span className='text-sm font-medium text-orange-800 dark:text-orange-300'>Escalated</span>
                    </div>
                  </div>
                )}

                {alert.acknowledgedBy && (
                  <div>
                    <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>
                      Acknowledged By
                    </label>
                    <p className='text-sm text-[var(--color-text-primary)]'>
                      {alert.acknowledgedByName ?? alert.acknowledgedBy}
                      {alert.acknowledgedAt && (
                        <span className='text-[var(--color-text-secondary)] ml-1'>
                          on {format(new Date(alert.acknowledgedAt), 'PPpp')}
                        </span>
                      )}
                    </p>
                  </div>
                )}

                {alert.resolvedBy && (
                  <div>
                    <label className='block text-sm font-medium text-[var(--color-text-secondary)]'>Resolved By</label>
                    <p className='text-sm text-[var(--color-text-primary)]'>
                      {alert.resolvedByName ?? alert.resolvedBy}
                      {alert.resolvedAt && (
                        <span className='text-[var(--color-text-secondary)] ml-1'>
                          on {format(new Date(alert.resolvedAt), 'PPpp')}
                        </span>
                      )}
                    </p>
                    {alert.resolutionNotes && (
                      <p className='text-sm text-[var(--color-text-secondary)] mt-1 p-2 bg-green-50 dark:bg-green-900/20 rounded'>
                        {alert.resolutionNotes}
                      </p>
                    )}
                  </div>
                )}

                {/* Resolve Form */}
                {showResolveForm && !alert.resolvedAt && (
                  <div className='p-4 bg-[var(--color-bg-subtle)] rounded-md space-y-3'>
                    <div>
                      <label
                        htmlFor='resolution'
                        className='block text-sm font-medium text-[var(--color-text-secondary)]'
                      >
                        Resolution <span className='text-red-500 dark:text-red-400'>*</span>
                      </label>
                      <textarea
                        id='resolution'
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        rows={3}
                        className='mt-1 block w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] dark:placeholder-gray-400 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                        placeholder='Describe how the issue was resolved...'
                      />
                    </div>
                    <div>
                      <label htmlFor='notes' className='block text-sm font-medium text-[var(--color-text-secondary)]'>
                        Additional Notes
                      </label>
                      <textarea
                        id='notes'
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        className='mt-1 block w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] dark:placeholder-gray-400 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                        placeholder='Optional additional notes...'
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                {historyEntries.length === 0 ? (
                  <p className='text-center text-[var(--color-text-secondary)] py-8'>No history available</p>
                ) : (
                  <div className='space-y-4'>
                    {historyEntries.map((entry, index) => (
                      <div key={index} className='flex gap-3'>
                        {getHistoryIcon(entry.action)}
                        <div className='flex-1'>
                          <p className='text-sm font-medium text-[var(--color-text-primary)] capitalize'>
                            {entry.action.replace('_', ' ')}
                          </p>
                          <p className='text-xs text-[var(--color-text-secondary)]'>
                            {format(new Date(entry.timestamp), 'PPpp')}
                          </p>
                          {entry.details && Object.keys(entry.details).length > 0 && (
                            <div className='mt-1 text-xs text-[var(--color-text-secondary)] bg-[var(--color-bg-subtle)] rounded p-2'>
                              {Object.entries(entry.details).map(([key, value]) => (
                                <div key={key}>
                                  <span className='font-medium'>{key}:</span> {String(value)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className='px-6 py-4 bg-[var(--color-bg-subtle)] border-t border-[var(--color-border)] flex justify-between'>
            <button
              onClick={onClose}
              className='px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-bg-subtle)]'
            >
              Close
            </button>
            <div className='flex gap-2'>
              {!alert.acknowledgedAt && !alert.resolvedAt && (
                <button
                  onClick={handleAcknowledge}
                  disabled={submitting}
                  className='px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 disabled:opacity-50'
                >
                  {submitting ? 'Processing...' : 'Acknowledge'}
                </button>
              )}
              {!alert.resolvedAt && !showResolveForm && (
                <button
                  onClick={() => setShowResolveForm(true)}
                  className='px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700'
                >
                  Resolve
                </button>
              )}
              {showResolveForm && (
                <>
                  <button
                    onClick={() => setShowResolveForm(false)}
                    className='px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-md hover:bg-[var(--color-bg-subtle)]'
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleResolve}
                    disabled={submitting || !resolution.trim()}
                    className='px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50'
                  >
                    {submitting ? 'Resolving...' : 'Confirm Resolve'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlertDetailModal;
